import React, { useState, lazy, Suspense, useMemo, memo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronRight,
  Globe,
  Star,
  X
} from 'lucide-react';
import { getBrazilianStatus } from '../../utils/detectBrazil';
import { gameExplorerHubPtBR } from '../mappings/gameExplorerHub.pt-br';

// Dynamic imports for code splitting
const BaseballDashMobile = lazy(() => import('../../pages/sport-dashboards/baseball/BaseballDashMobile'));
const FootballDashMobile = lazy(() => import('../../pages/sport-dashboards/football/footballHubMobile'));
const MMADashMobile = lazy(() => import('../../pages/sport-dashboards/mma/MMADashMobile'));
const NFLDashMobile = lazy(() => import('../../pages/sport-dashboards/NFL/NFLDashMobile'));
const NCAAFDashMobile = lazy(() => import('../../pages/sport-dashboards/ncaaf/NCAAFDashMobile'));

// Loading component for suspense
const DashboardLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
  </div>
);

// Level color and label semantics helper - Terminal style
const getLevelConfig = (level) => {
  switch(level?.toLowerCase()) {
    case 'elite':
      return { color: 'text-violet-400/90', badge: 'bg-violet-500/[0.08] border-violet-500/20' };
    case 'pro+':
      return { color: 'text-emerald-400/80', badge: 'bg-emerald-500/[0.05] border-emerald-500/20' };
    case 'pro':
      return { color: 'text-white/70', badge: 'bg-white/[0.03] border-white/[0.08]' };
    default:
      return { color: 'text-white/40', badge: 'bg-white/[0.02] border-white/[0.05]' };
  }
};

// Sport module card component - dense and professional
const SportModuleCard = memo(({
  sport,
  onSelect,
  isAvailable = true,
  t
}) => {
  const levelConfig = getLevelConfig(sport.level);

  // Fallback translation function if not provided
  const translate = t || ((text) => text);

  return (
    <motion.button
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={isAvailable ? { scale: 0.98 } : {}}
      onClick={() => isAvailable && onSelect(sport)}
      disabled={!isAvailable}
      className={`
        relative w-full h-20 p-3 rounded-lg
        bg-white/[0.02] backdrop-blur-sm
        border border-white/[0.05]
        transition-all duration-200
        ${isAvailable ? 'hover:bg-white/[0.03] hover:border-white/[0.08] active:scale-[0.99]' : 'opacity-40'}
      `}
      aria-label={`${translate('Open module')} — ${sport.name} — ${translate('Level')} ${sport.level}`}
      role="button"
      tabIndex={isAvailable ? 0 : -1}
    >
      <div className="flex items-center h-full">
        {/* Left cluster - Icon in soft tile */}
        <div className="flex-shrink-0 mr-3">
          <div className={`w-8 h-8 rounded bg-white/[0.03] border border-white/[0.05] flex items-center justify-center`}>
            <span className="text-[8px] font-mono font-medium text-white/40">
              {sport.textIcon}
            </span>
          </div>
        </div>
        
        {/* Content stack - single lines only */}
        <div className="flex-1 text-left min-w-0">
          <h4 className="text-[11px] font-medium tracking-[0.15em] text-white/80 uppercase leading-[1.2] truncate">
            {sport.name}
          </h4>
          <div className="mt-0.5 text-[10px] text-white/50 font-mono truncate">
            <span>{sport.coverage}</span>
            <span className="mx-1.5 text-white/20">•</span>
            <span>{sport.modelType}</span>
          </div>
          <div className="mt-1 flex items-center gap-1.5">
            <div className={`inline-flex items-center px-1.5 py-0.5 rounded border ${levelConfig.badge}`}>
              <span className={`text-[9px] font-mono font-medium uppercase tracking-wider ${levelConfig.color}`}>
                {sport.level}
              </span>
            </div>
          </div>
        </div>
        
        {/* Right affordance - lighter chevron */}
        {isAvailable && (
          <div className="flex-shrink-0 ml-2">
            <div className="w-6 h-6 rounded bg-white/[0.02] border border-white/[0.03] flex items-center justify-center">
              <ChevronRight className="w-3 h-3 text-white/30" />
            </div>
          </div>
        )}
      </div>
    </motion.button>
  );
});

/**
 * Game Explorer Hub - Professional mobile slide-over panel
 * Modal sheet semantics with proper density and spacing
 */
const GameExplorerHub = ({ onClose }) => {
  const [selectedSport, setSelectedSport] = useState(null);
  const [activeView, setActiveView] = useState('hub');
  
  // Disable background scroll when sheet is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, []);
  
  // Check if user is Brazilian and set up translation
  const isBrazilian = getBrazilianStatus();
  const t = useCallback((text) => {
    if (!isBrazilian) return text;
    return gameExplorerHubPtBR[text] || text;
  }, [isBrazilian]);

  // Memoized intelligence modules configuration
  const intelligenceModules = useMemo(() => [
    {
      id: 'baseball',
      name: t('Baseball'),
      textIcon: 'MLB',
      coverage: t('Complete'),
      modelType: 'Cognitive Model',
      level: 'Elite',
      component: BaseballDashMobile,
      available: true
    },
    {
      id: 'soccer',
      name: t('Soccer'),
      textIcon: 'FCB',
      coverage: t('103+ Leagues'),
      modelType: 'AI Service Mesh',
      level: 'Elite',
      component: FootballDashMobile,
      available: true
    },
    {
      id: 'mma',
      name: t('MMA'),
      textIcon: 'UFC',
      coverage: t('UFC'),
      modelType: 'Cognitive Model',
      level: 'Pro',
      component: MMADashMobile,
      available: true
    },
    {
      id: 'nfl',
      name: t('NFL'),
      textIcon: 'NFL',
      coverage: t('Complete'),
      modelType: 'AI Service Mesh',
      level: 'Pro+',
      component: NFLDashMobile,
      available: true
    },
    {
      id: 'ncaaf',
      name: t('NCAAF'),
      textIcon: 'NCAA',
      coverage: t('Complete'),
      modelType: 'Cognitive Model',
      level: 'Pro',
      component: NCAAFDashMobile,
      available: true
    }
  ], [t]);

  const handleSportSelect = useCallback((sport) => {
    setSelectedSport(sport);
    setActiveView('sport');
  }, []);

  const handleBackToHub = useCallback(() => {
    setActiveView('hub');
    setSelectedSport(null);
  }, []);

  // Render the selected sport's dashboard - Full screen without wrapper header
  if (activeView === 'sport' && selectedSport) {
    const SportComponent = selectedSport.component;
    return (
      <>
        {/* Background scrim for consistency */}
        <motion.div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleBackToHub}
        />
        
        {/* Sport Dashboard - rendered directly without wrapper */}
        <motion.div 
          className="fixed inset-0 z-[9999]"
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        >
          {/* Sport Dashboard renders its own complete UI with navigation */}
          <Suspense fallback={
            <div className="flex items-center justify-center h-screen bg-gradient-to-b from-slate-900 to-black">
              <DashboardLoader />
            </div>
          }>
            <SportComponent />
          </Suspense>
        </motion.div>
      </>
    );
  }

  // Hub View - Side panel semantics (slides from right)
  return (
    <>
      {/* Background scrim */}
      <motion.div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />
      
      {/* Side panel - Terminal glassmorphism */}
      <motion.div
        className="fixed inset-y-0 right-0 w-full z-[9999] flex flex-col bg-black/95 backdrop-blur-lg"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 280 }}
      >
        {/* Top safe area bumper */}
        <div className="h-12 bg-black/90" />

        {/* Header Block - Terminal style */}
        <div className="bg-black/90 border-b border-white/[0.05]">
          <div className="pt-3 px-3">
            {/* Close button - top right */}
            <button
              onClick={onClose}
              className="absolute top-[3.75rem] right-3 w-7 h-7 flex items-center justify-center bg-white/[0.02] hover:bg-white/[0.05] rounded border border-white/[0.03] transition-all"
              aria-label={t('Fechar')}
            >
              <X className="w-3.5 h-3.5 text-white/50" />
            </button>
            
            {/* Header content */}
            <div className="flex items-start gap-2.5 pb-2.5">
              <div className="w-7 h-7 rounded bg-gradient-to-r from-violet-500/[0.08] to-violet-600/[0.08] border border-violet-500/20 flex items-center justify-center">
                <Globe className="w-3.5 h-3.5 text-violet-400/70" />
              </div>
              <div className="flex-1 pr-8">
                <h2 className="text-[13px] font-semibold text-white/40 uppercase tracking-[0.2em] leading-[1.2]">
                  {t('INTELLIGENCE HUB')}
                </h2>
                <p className="text-[10px] text-white/20 mt-0.5 font-mono">
                  {t('Advanced Analytics & Prediction Models')}
                </p>
              </div>
            </div>
          </div>
          
          {/* Terminal divider */}
          <div className="h-px bg-white/[0.05]" />
          
          {/* Meta row - Terminal style chips */}
          <div className="px-3 py-1.5">
            <div className="flex gap-1.5">
              <div className="inline-flex items-center h-5 px-2 rounded bg-white/[0.02] border border-white/[0.03]">
                <span className="text-[9px] text-white/30 font-mono uppercase">{t('LVL:')}</span>
                <span className={`ml-1 text-[9px] font-mono uppercase ${getLevelConfig('Elite').color}`}>ELITE</span>
              </div>
              <div className="inline-flex items-center h-5 px-2 rounded bg-white/[0.02] border border-white/[0.03]">
                <span className="text-[9px] text-white/30 font-mono uppercase">{t('GLOBAL')}</span>
              </div>
              <div className="inline-flex items-center h-5 px-2 rounded bg-white/[0.02] border border-white/[0.03]">
                <span className="text-[9px] text-white/30 font-mono uppercase">{t('AI')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content - Terminal precision */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-3 py-3">
            {/* Section label - Terminal style */}
            <div className="mb-2">
              <h3 className="text-[10px] font-medium text-white/30 uppercase tracking-[0.3em]">
                {t('INTELLIGENCE MODULES')}
              </h3>
              <div className="h-px bg-white/[0.03] mt-1" />
            </div>
            
            {/* Intelligence Modules - Tight spacing */}
            <div className="space-y-2">
              {intelligenceModules.map((sport) => (
                <SportModuleCard
                  key={sport.id}
                  sport={sport}
                  onSelect={handleSportSelect}
                  isAvailable={sport.available}
                  t={t}
                />
              ))}
            </div>

            {/* Section spacing */}
            <div className="h-3" />

            {/* Additional Sports Block */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <div className="p-2.5 rounded-lg bg-white/[0.02] backdrop-blur-sm border border-white/[0.05]">
                <h4 className="text-[10px] font-medium text-white/30 uppercase tracking-[0.3em] mb-1.5">
                  {t('ADDITIONAL SPORTS')}
                </h4>
                <div className="flex gap-1 flex-wrap">
                  {['NBA', 'NHL', 'Tennis', 'NCAAB'].map((sport) => (
                    <div
                      key={sport}
                      className="h-5 px-2 flex items-center rounded bg-white/[0.02] border border-white/[0.03]"
                    >
                      <span className="text-[9px] text-white/40 font-mono uppercase">
                        {t(sport)}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-[9px] text-white/20 mt-1.5 font-mono">
                  {t('Advanced models in development')}
                </p>
              </div>
            </motion.div>

            {/* AI Prediction Suite Feature Card - same component as modules */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35 }}
              className="mt-2"
            >
              <div className="h-20 p-3 rounded-lg bg-gradient-to-r from-violet-500/[0.08] to-violet-600/[0.08] backdrop-blur-sm border border-violet-500/20">
                <div className="flex items-center h-full">
                  <div className="flex-shrink-0 mr-3">
                    <div className="w-8 h-8 rounded bg-white/[0.03] border border-white/[0.05] flex items-center justify-center">
                      <Star className="w-4 h-4 text-violet-400/70" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-[11px] font-medium tracking-[0.15em] text-white/80 uppercase leading-[1.2]">
                      {t('AI PREDICTION SUITE')}
                    </h4>
                    <p className="text-[10px] text-white/50 font-mono mt-0.5">
                      {t('7 Advanced Models')} • {t('Professional Analytics')}
                    </p>
                  </div>
                  <div className="flex-shrink-0 ml-2">
                    <div className="w-6 h-6 rounded bg-white/[0.02] border border-white/[0.03] flex items-center justify-center">
                      <ChevronRight className="w-3 h-3 text-white/30" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* Bottom safe area padding - ensures last item clears bottom nav */}
          <div className="h-[calc(env(safe-area-inset-bottom,0px)+24px)]" />
        </div>
      </motion.div>
    </>
  );
};

export default memo(GameExplorerHub);